import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Product } from '../common';
import { getQueryParam } from '../utils/getQueryParam';
import { getTokenIssuedAt } from '../utils/getTokenIssuedAt';

type StateByKey = {
  count: number;
  market: string;
  shownInCurrentSession: boolean;
};

export type ShownByUserState = {
  [key: string]: {
    [key in STATE_KEY]?: StateByKey;
  };
};

export type LastShownAtByUserState = {
  [key in string]?: number;
};

type ShownByMarket = { shown: boolean; market?: { itemGroupId: string; countryId: string }; count?: number };

type ReturnType = {
  loading: boolean;
  upsellShownByMarket: ShownByMarket;
  updateLocalState: (market: string, value: boolean, viewed?: boolean) => void;
  onShowInCurrentSession: () => void;
};

export enum STATE_KEY {
  LAST_UPSELL_SHOWN_AT = 'last_upsell_shown_at',
  INDUSTRY_CONSUMER_LOST_SHOPPER = 'industry_consumer_lost_shopper_upsell_shown_state',
  RETAIL_CONSUMER_LOST_SHOPPER = 'retail_consumer_lost_shopper_upsell_shown_state',
  CONSUMER_STORIES_RETAIL_LOST_SHOPPER = 'consumer_stories_retail_lost_shopper_upsell_shown_state',
}

export enum COUNT_DO_NOT_SHOW {
  DEFAULT = 0,
  INDUSTRY_CONSUMER_LOST_SHOPPER = 2,
  RETAIL_CONSUMER_LOST_SHOPPER = 2,
  CONSUMER_STORIES_RETAIL_LOST_SHOPPER = 2,
}

export const useUpsellLocalStorage = (products: Product[] = []): ReturnType => {
  const pathname = window.location.pathname;
  const { user, isAuthenticated } = useAuthentication();
  const [upsellShownByUser, setUpsellShownByUser] = useState<ShownByUserState>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [keyState, setKeyState] = useState<string | null>(null);
  const [lastShownAtState, setLastShownAtState] = useState<LastShownAtByUserState>({});
  const [lastShownAtLoading, setLastShownAtLoading] = useState<boolean>(true);
  const [upsellShownByMarket, setUpsellShownByMarket] = useState<ShownByMarket>({ shown: false });
  const utmCampaign = getQueryParam('utm_campaign');

  const onShowInCurrentSession = useCallback(() => {
    const userState = upsellShownByUser[user.sub] ?? {};
    const stateByKeyState = userState[keyState] ?? {};
    if (lastShownAtLoading) {
      return;
    }

    const handle = async () => {
      const tokenIssuedAt = await getTokenIssuedAt();

      if (stateByKeyState.shownInCurrentSession && stateByKeyState.shownInCurrentSessionAt === tokenIssuedAt) {
        return;
      }
      const newCount = stateByKeyState.count === undefined ? 0 : stateByKeyState.count + 1;
      stateByKeyState.shownInCurrentSession = true;
      stateByKeyState.shownInCurrentSessionAt = tokenIssuedAt;
      stateByKeyState.count = newCount;

      const newState = {
        ...upsellShownByUser,
        [user?.sub]: {
          ...userState,
          [keyState]: {
            ...stateByKeyState,
          },
        },
      };
      writeState(newState, keyState, lastShownAtState);
      setUpsellShownByUser(newState);
    };

    handle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyState, lastShownAtLoading, lastShownAtState, upsellShownByUser, user?.sub]);

  useEffect(() => {
    let key = null;
    if (products.includes(Product.Market)) {
      key = STATE_KEY.INDUSTRY_CONSUMER_LOST_SHOPPER;
    }
    if (products.includes(Product.Retail)) {
      key = STATE_KEY.CONSUMER_STORIES_RETAIL_LOST_SHOPPER;
    }
    setKeyState(key);
  }, [products]);

  const getShownLimitByKeyState = useCallback((keyState: STATE_KEY): number => {
    if ([STATE_KEY.CONSUMER_STORIES_RETAIL_LOST_SHOPPER].includes(keyState)) {
      return COUNT_DO_NOT_SHOW.CONSUMER_STORIES_RETAIL_LOST_SHOPPER;
    }

    if ([STATE_KEY.RETAIL_CONSUMER_LOST_SHOPPER, STATE_KEY.INDUSTRY_CONSUMER_LOST_SHOPPER].includes(keyState)) {
      return COUNT_DO_NOT_SHOW.INDUSTRY_CONSUMER_LOST_SHOPPER;
    }

    return COUNT_DO_NOT_SHOW.DEFAULT;
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) {
      return setUpsellShownByMarket({ shown: false });
    }

    const updateState = async () => {
      const userState = upsellShownByUser[user.sub] ?? {};
      const defaultShown = Object.keys(userState)
        .map((key: string) => userState[key])
        .find(ks => ks.market);
      let result: ShownByMarket = {
        shown: !!defaultShown,
        count: defaultShown?.count ?? 0,
      };
      if (
        [
          STATE_KEY.CONSUMER_STORIES_RETAIL_LOST_SHOPPER,
          STATE_KEY.RETAIL_CONSUMER_LOST_SHOPPER,
          STATE_KEY.INDUSTRY_CONSUMER_LOST_SHOPPER,
        ].includes(keyState as STATE_KEY)
      ) {
        const tokenIssuedAt = await getTokenIssuedAt();
        const shownDuringCurrentLoginSession = lastShownAtLoading || lastShownAtState[user.sub] === tokenIssuedAt;
        const shownLimit = getShownLimitByKeyState(keyState as STATE_KEY);
        result = {
          shown: (userState[keyState]?.count ?? 0) >= shownLimit || shownDuringCurrentLoginSession,
          market: userState[keyState]?.market
            ? [userState[keyState].market].reduce((_res, market) => {
                const [itemGroupId, countryId] = [...market.split('/')];
                return { itemGroupId, countryId };
              }, {} as { itemGroupId: string; countryId: string })
            : undefined,
          count: userState[keyState]?.count ?? 0,
        };
      }
      setUpsellShownByMarket(result);
    };

    updateState();
  }, [
    isAuthenticated,
    keyState,
    lastShownAtLoading,
    lastShownAtState,
    upsellShownByUser,
    user?.sub,
    getShownLimitByKeyState,
  ]);

  const readLastShownAtState = useCallback(() => {
    if (!isAuthenticated || !user?.sub) {
      return;
    }
    const checkState = async () => {
      const state = localStorage.getItem(STATE_KEY.LAST_UPSELL_SHOWN_AT);
      const tokenIssuedAt = await getTokenIssuedAt();
      let newState = {};
      if (state) {
        newState = JSON.parse(state);
      }
      if (utmCampaign) {
        newState = {
          ...newState,
          [user.sub]: tokenIssuedAt,
        };
      }
      if (Object.keys(newState)) {
        setLastShownAtState(newState);
        localStorage.setItem(STATE_KEY.LAST_UPSELL_SHOWN_AT, JSON.stringify(newState));
      }
    };

    checkState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, keyState, upsellShownByUser, user?.sub, utmCampaign]);

  const readState = (key: string) => {
    const state = localStorage.getItem(key);
    if (state) {
      setUpsellShownByUser(JSON.parse(state));
    }
  };

  const writeState = useCallback((state: ShownByUserState, key: string, lastShownState: LastShownAtByUserState) => {
    localStorage.setItem(key, JSON.stringify(state));
    localStorage.setItem(STATE_KEY.LAST_UPSELL_SHOWN_AT, JSON.stringify(lastShownState));
  }, []);

  const doNotShowCountByKeyState = useMemo(() => {
    let count = COUNT_DO_NOT_SHOW.DEFAULT;
    if (keyState === STATE_KEY.INDUSTRY_CONSUMER_LOST_SHOPPER) {
      count = COUNT_DO_NOT_SHOW.INDUSTRY_CONSUMER_LOST_SHOPPER;
    }
    if (keyState === STATE_KEY.CONSUMER_STORIES_RETAIL_LOST_SHOPPER) {
      count = COUNT_DO_NOT_SHOW.CONSUMER_STORIES_RETAIL_LOST_SHOPPER;
    }
    if (keyState === STATE_KEY.RETAIL_CONSUMER_LOST_SHOPPER) {
      count = COUNT_DO_NOT_SHOW.RETAIL_CONSUMER_LOST_SHOPPER;
    }
    return count;
  }, [keyState]);

  const updateLocalState = useCallback(
    (market: string, _value: boolean, viewed: boolean = false) => {
      if (!isAuthenticated || !user?.sub || !keyState) {
        return;
      }

      const update = async () => {
        const storageItemString = localStorage.getItem(keyState);
        const storageItem = storageItemString ? JSON.parse(storageItemString) : {};
        const storageItemCount = storageItem[user.sub]?.[keyState]?.count;

        const userState = upsellShownByUser[user.sub] ?? {};
        const stateByKeyState = userState[keyState] ?? {};
        const count = storageItemCount ?? stateByKeyState.count ?? 0;
        const userKeyStateCount = viewed ? doNotShowCountByKeyState : count;
        const tokenIssuedAt = await getTokenIssuedAt();
        const newState = {
          ...upsellShownByUser,
          [user.sub]: {
            ...userState,
            [keyState]: {
              ...stateByKeyState,
              market: stateByKeyState.market ?? market,
              count: userKeyStateCount,
              shownInCurrentSession: true,
              shownInCurrentSessionAt: tokenIssuedAt,
            },
          },
        };
        const newLastShownAtState = {
          ...lastShownAtState,
          [user.sub]: tokenIssuedAt,
        };
        writeState(newState, keyState, newLastShownAtState);
        setUpsellShownByUser(newState);
        setLastShownAtState(newLastShownAtState);
      };

      update();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, keyState, upsellShownByUser, doNotShowCountByKeyState, getTokenIssuedAt, writeState]
  );

  useEffect(() => {
    if (keyState) {
      readState(keyState);
      setLoading(false);
    }
  }, [pathname, keyState]);

  useEffect(() => {
    readLastShownAtState();
    setLastShownAtLoading(false);
  }, [readLastShownAtState]);

  return useMemo(
    () => ({
      loading,
      upsellShownByMarket,
      updateLocalState,
      onShowInCurrentSession,
    }),
    [loading, upsellShownByMarket, updateLocalState, onShowInCurrentSession]
  );
};
